import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Button } from "../../components/ui/Button";
import { CopyField } from "../../components/ui/CopyField";
import { Field } from "../../components/ui/Field";
import { ErrorText } from "../../components/ui/feedback";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { useI18n } from "../../i18n/I18nProvider";
import { apiRequest } from "../../lib/api";
import type { CreatedApiKey } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";

export interface CreateKeyModalProps {
  workspaceId: string;
  configurationId: string;
  onClose: () => void;
}

/**
 * Name a new key, then show it once. The reveal step cannot be dismissed with Escape or the close
 * button, so the key is copied before the dialog goes away.
 */
export function CreateKeyModal({ workspaceId, configurationId, onClose }: CreateKeyModalProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState<CreatedApiKey | null>(null);

  const create = useMutation({
    mutationFn: (value: string) =>
      apiRequest<CreatedApiKey>(paths.apiKeys(workspaceId, configurationId), {
        method: "POST",
        body: { label: value },
      }),
    onSuccess: (result) => {
      setCreated(result);
      return queryClient.invalidateQueries({
        queryKey: ["api-keys", workspaceId, configurationId],
      });
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = label.trim();
    if (trimmed) create.mutate(trimmed);
  };

  if (created) {
    return (
      <Modal title={t("keys.dialogTitle")} onClose={onClose} dismissible={false}>
        <div className="space-y-4">
          <p className="text-operate font-semibold text-red">{t("keys.wontSeeAgain")}</p>
          <p className="text-note text-ink-2">{t("keys.createdOnce")}</p>
          <CopyField value={created.key} />
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>
              {t("keys.done")}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={t("keys.dialogTitle")} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("keys.label")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={label}
              maxLength={80}
              onChange={(event) => setLabel(event.target.value)}
            />
          )}
        </Field>
        <ErrorText error={create.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={create.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={create.isPending}
            disabled={label.trim() === ""}
          >
            {t("keys.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
